import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "@/lib/auth";
import { consumirSessaoExpirada } from "@/lib/api/session-guard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Clock } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import logoDobraImg from "@/assets/logo-dobra.gif";
import confeccaoArt from "@/assets/confeccao-art.png";

export default function Auth() {
  const navigate = useNavigate();
  // Chegou aqui porque o servidor recusou o token (session-guard). Dizer isso
  // evita a pior leitura possível: achar que o sistema "deslogou sozinho" — ou,
  // pior, ficar no quadro zerado achando que a fábrica parou.
  const [searchParams] = useSearchParams();
  // Dois caminhos chegam aqui: o redirecionamento do session-guard (query
  // string) e a carga de página com token vencido, onde quem redireciona é o
  // ProtectedRoute e o motivo vem pelo bilhete. `useState` com inicializador
  // porque consumir o bilhete tem efeito colateral — em StrictMode um cálculo
  // solto rodaria duas vezes e o aviso sumiria antes de aparecer.
  const [sessaoExpirada] = useState(
    () => searchParams.get('sessao') === 'expirada' || consumirSessaoExpirada(),
  );
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      toast.success("Login realizado com sucesso!");
      navigate("/");
    } catch (error: any) {
      toast.error(error.message || "Erro ao processar autenticação");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start bg-gradient-to-br from-primary/10 via-background to-secondary/10 p-4 pt-12 relative">
      {/* Toggle de Tema - canto superior direito */}
      <div className="absolute top-4 right-4">
        <ThemeToggle variant="icon" />
      </div>
      
      <div className="w-full max-w-md space-y-6 flex-shrink-0">
        {/* Logo da Dobra - Destaque Principal */}
        <div className="flex justify-center">
          <img 
            src={logoDobraImg} 
            alt="Dobra Camisetas Personalizadas" 
            className="h-20 w-auto object-contain opacity-95 hover:opacity-100 transition-opacity drop-shadow-lg"
          />
        </div>
        
        {/* Card de Login */}
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              SmartProd
            </CardTitle>
            <CardDescription className="text-center font-medium">
              by Dobra Camisetas
            </CardDescription>
            <CardDescription className="text-center text-xs opacity-70">
              Entre com suas credenciais
            </CardDescription>
            {sessaoExpirada && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                <Clock className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <strong>Sua sessão expirou.</strong> Entre de novo para
                  continuar — nenhum dado foi perdido.
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAuth} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Entrar
              </Button>

              <Button
                type="button"
                variant="link"
                className="w-full text-sm"
                onClick={() => navigate('/auth/reset-password')}
              >
                Esqueceu a senha?
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      
      {/* Arte Decorativa - Universo de Confecção */}
      <div className="flex-1 w-full flex items-end justify-center pb-8 pointer-events-none">
        <img 
          src={confeccaoArt} 
          alt="Confecção e Estamparia Dobra" 
          className="max-w-2xl w-full h-auto opacity-20 object-contain"
        />
      </div>
    </div>
  );
}
